import type { OrganizationSettingsPatchParams, PaymentMethod } from "@mountaya/client";
import {
  keepPreviousData,
  type QueryClient,
  useIsMutating,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import { invoicesQueryKey } from "../../billing/rules/queries";
import { CACHE_PRESETS } from "../../rest/rules/queryClient";
import {
  isPaymentRequiredError,
  isPreconditionFailedError,
  isStaffControlledError,
  isWriteMadeOrRunning,
  type RestClientConfig,
} from "../../rest/rules/sdk";
import type { components } from "../../schema/api";
import { userOrganizationsQueryKey } from "../../users/rules/organizations";
import {
  confirmOrganizationBillingPaymentMethod,
  endOrganizationBillingSubscription,
  runOrganizationBillingSubscription,
} from "./billing";
import type { LimitValue } from "./entitlements";
import {
  selectCanManageBilling,
  selectHasCapability,
  selectLimit,
  selectRequiresPaymentMethod,
} from "./entitlements";
import { inviteOrganizationMembers } from "./invitations";
import { organizationLookupQueryOptions } from "./lookup";
import {
  createOrganization,
  createPublishableKey,
  createSecretKey,
  deletePublishableKey,
  deleteSecretKey,
  removeOrganizationMember,
  revokeOrganizationInvitation,
  transferOrganizationOwnership,
  updateOrganization,
  updateOrganizationMemberRole,
  updateOrganizationSettings,
  updatePublishableKey,
  updateSecretKey,
} from "./mutations";
import type {
  ConsoleOrganization,
  ConsoleOrganizationSettings,
  ConsolePublishableKey,
  EntitlementLimitKey,
  OrganizationAccount,
  OrganizationSubscriptionSummary,
  ProductCapability,
  SecretKey,
} from "./queries";
import {
  organizationAccountQueryKey,
  organizationAccountQueryOptions,
  organizationInvitationsQueryKey,
  organizationInvitationsQueryOptions,
  organizationMembersQueryKey,
  organizationMembersQueryOptions,
  organizationQueryKey,
  organizationQueryOptions,
  organizationSchema,
  organizationSettingsSchema,
  publishableKeySchema,
  publishableKeysQueryKey,
  publishableKeysQueryOptions,
  secretKeySchema,
  secretKeysQueryKey,
  secretKeysQueryOptions,
} from "./queries";
import type { AssignableOrganizationRole } from "./roles";
import type { UsageDataFilters, UsageTilesFilters } from "./usage";
import { organizationUsageDataQueryOptions, organizationUsageTilesQueryOptions } from "./usage";

type OrganizationBillingPaymentMethodParams =
  components["schemas"]["OrganizationBillingPaymentMethodParams"];

/**
 * useOrganizationLookup resolves an organization from a publishable key via the
 * public lookup endpoint. The studio reads the root organization this way to pull
 * its control-visibility defaults and map restrictions; the result changes only
 * when an admin edits the org settings, so it rides the static preset's long
 * staleTime/gcTime. Keyed by publishable key, so distinct keys never share a
 * cache entry. The query is gated on a non-empty key, so a deployment without a
 * configured root publishable key no-ops cleanly (no request, no error) and the
 * studio degrades to unconstrained, full-featured defaults.
 */
export const useOrganizationLookup = (config: RestClientConfig, publishableKey: string) =>
  useQuery({
    ...organizationLookupQueryOptions(config, publishableKey),
    enabled: Boolean(publishableKey),
    gcTime: CACHE_PRESETS.static.gcTime,
    staleTime: CACHE_PRESETS.static.staleTime,
  });

/**
 * useOrganization reads one organization by id for the IAM Settings/Security
 * pages. It rides the default standard preset (a short staleTime): invalidation
 * after a settings/profile edit is what keeps it fresh, and a longer stale window
 * would hide changes another admin made. Gated on a non-empty id so the hook
 * no-ops before the route resolves the organization.
 */
export const useOrganization = (config: RestClientConfig, organizationId: string) =>
  useQuery({
    ...organizationQueryOptions(config, organizationId),
    enabled: Boolean(organizationId),
  });

/**
 * useOrganizationAccount reads the account behind an organization for the console: the
 * entitlements its agreement grants, how much of each counted limit is in use, its subscription
 * summary and whether the signed-in user may change its billing. The public organization read
 * carries none of it. Standard preset; gated on a non-empty id so the hook no-ops before the
 * route resolves the organization.
 */
export const useOrganizationAccount = (config: RestClientConfig, organizationId: string) =>
  useQuery({
    ...organizationAccountQueryOptions(config, organizationId),
    enabled: Boolean(organizationId),
  });

/**
 * useHasCapability reads whether the organization holds a product capability,
 * gating a studio/publishing/tiles/data feature on the server-side entitlement.
 * Entitlements are server state, so it projects the account query result (the same
 * cache entry useOrganizationAccount reads) rather than the client identity store — the
 * gate reads ONLY the server entitlement, never the individual isOrganizationMember
 * pro flag. While the account is still loading it resolves to false, so a feature stays
 * gated until the grant is confirmed.
 */
export const useHasCapability = (
  config: RestClientConfig,
  organizationId: string,
  capability: ProductCapability,
): boolean => {
  const account = useOrganizationAccount(config, organizationId);

  return selectHasCapability(account.data, capability);
};

/**
 * useLimit reads one of the seven limits of the organization's agreement (seats, published
 * itineraries and zones, the monthly and per-minute request allowances) off its account into a
 * LimitValue, for display: `unlimited` for a null value, otherwise the numeric ceiling.
 * Projects the account query result so the usage-vs-limit meters read the server entitlement,
 * not a client flag.
 */
export const useLimit = (
  config: RestClientConfig,
  organizationId: string,
  key: EntitlementLimitKey,
): LimitValue => {
  const account = useOrganizationAccount(config, organizationId);

  return selectLimit(account.data, key);
};

/**
 * useCanManageBilling reads the account's `can_manage_billing` flag — true only for
 * an organization owner or admin. The billing page renders its edit controls only
 * when this is true; a contributor/viewer sees the read-only surface. Resolves to
 * false while the account loads or for a non-admin, so the controls stay hidden until
 * the membership is confirmed.
 */
export const useCanManageBilling = (config: RestClientConfig, organizationId: string): boolean => {
  const account = useOrganizationAccount(config, organizationId);

  return selectCanManageBilling(account.data);
};

/**
 * useRequiresPaymentMethod reads whether a self-serve organization needs a card on file for
 * the Data API and the MCP server to answer: no payment method yet, whether or not its first
 * card has opened the Data API subscription. It projects both halves of the organization (its
 * `awaiting_agreement` off the organization read, the rest off the account, refreshed by the
 * payment-method confirm invalidation), so the proactive nudge clears the instant a card is
 * attached. Resolves to false while either loads, for an organization billed by agreement or
 * awaiting one, for a delinquent subscription (its own banner state handles it), and for an org
 * that already has a card.
 */
export const useRequiresPaymentMethod = (
  config: RestClientConfig,
  organizationId: string,
): boolean => {
  const organization = useOrganization(config, organizationId);
  const account = useOrganizationAccount(config, organizationId);

  return selectRequiresPaymentMethod(organization.data, account.data);
};

/**
 * useOrganizationMembers reads an organization's member list for the Members
 * page. Standard preset; gated on a non-empty id.
 */
export const useOrganizationMembers = (config: RestClientConfig, organizationId: string) =>
  useQuery({
    ...organizationMembersQueryOptions(config, organizationId),
    enabled: Boolean(organizationId),
  });

/**
 * useOrganizationInvitations reads the invitations to an organization still waiting for an
 * answer, for the members page. Standard preset; gated on a non-empty id.
 */
export const useOrganizationInvitations = (config: RestClientConfig, organizationId: string) =>
  useQuery({
    ...organizationInvitationsQueryOptions(config, organizationId),
    enabled: Boolean(organizationId),
  });

/**
 * usePublishableKeys reads an organization's publishable keys for the API keys
 * page. Standard preset; gated on a non-empty id.
 */
export const usePublishableKeys = (config: RestClientConfig, organizationId: string) =>
  useQuery({
    ...publishableKeysQueryOptions(config, organizationId),
    enabled: Boolean(organizationId),
  });

/**
 * useSecretKeys reads an organization's secret keys for the API keys page. Listed
 * keys never carry the secret value. Standard preset; gated on a non-empty id.
 */
export const useSecretKeys = (config: RestClientConfig, organizationId: string) =>
  useQuery({
    ...secretKeysQueryOptions(config, organizationId),
    enabled: Boolean(organizationId),
  });

/**
 * useOrganizationUsageTiles reads an organization's Tile API usage series for the
 * given filter. keepPreviousData holds the prior series on a filter change so the
 * usage charts do not blank while the new range loads. Gated on a non-empty id.
 */
export const useOrganizationUsageTiles = (
  config: RestClientConfig,
  organizationId: string,
  filters: UsageTilesFilters,
) =>
  useQuery({
    ...organizationUsageTilesQueryOptions(config, organizationId, filters),
    enabled: Boolean(organizationId),
    placeholderData: keepPreviousData,
  });

/**
 * useOrganizationUsageData reads an organization's Data API usage series for the
 * given filter, with the same keepPreviousData behavior as the tile usage hook.
 * Gated on a non-empty id.
 */
export const useOrganizationUsageData = (
  config: RestClientConfig,
  organizationId: string,
  filters: UsageDataFilters,
) =>
  useQuery({
    ...organizationUsageDataQueryOptions(config, organizationId, filters),
    enabled: Boolean(organizationId),
    placeholderData: keepPreviousData,
  });

/**
 * useCreateOrganization creates a new organization, under the `idempotencyKey` the caller holds
 * across its resubmits when it passes one. Once it settles it invalidates the user's organization
 * list, so the new org appears in the switcher/nav, and on a failure too: a create whose answer
 * was lost after it committed is an organization the person now belongs to. What follows the
 * create (the card setup of a self-serve organization) is the caller's, which reads the resolved
 * organization off mutateAsync; a thrown MountayaApiError surfaces inline in the create form.
 */
export const useCreateOrganization = (config: RestClientConfig) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      idempotencyKey,
      ...params
    }: Parameters<typeof createOrganization>[1] & { readonly idempotencyKey?: string }) =>
      createOrganization(config, params, idempotencyKey),
    onSettled: () => queryClient.invalidateQueries({ queryKey: userOrganizationsQueryKey }),
  });
};

/**
 * ConditionalWrite is what a conditional write's mutation takes: the change, and the `etag` the
 * resource was read under, sent as its `ifMatch`. The caller passes the tag of the read its form
 * was seeded from, so a write made over a change somebody else made since is refused with a 412
 * rather than silently undoing it.
 */
export interface ConditionalWrite<P> {
  readonly ifMatch: string | undefined;
  readonly params: P;
}

/**
 * The writers below put what a write answered with into the read it changed, before that read is
 * asked again. A conditional write's answer carries the new `etag`, so a write made next on the
 * same resource (a second card of the same page saved while the refetch of the first is still
 * out, or one sent after that refetch failed) is conditional on the version this write made
 * rather than refused for the one before it; a revoked key's answer carries its `revoked_at`, so
 * the key leaves the page's live keys whether or not that refetch lands. Each answer is parsed as
 * the read parses it, so the entry holds what a refetch would; one that does not parse is left to
 * that refetch, since a write that landed is never reported as failed.
 */

/** writeAnsweredOrganization puts the organization a rename answered with into its read. */
const writeAnsweredOrganization = (
  queryClient: QueryClient,
  organizationId: string,
  answer: unknown,
): void => {
  const answered = organizationSchema.safeParse(answer);
  if (answered.success) {
    queryClient.setQueryData<ConsoleOrganization>(
      organizationQueryKey(organizationId),
      answered.data,
    );
  }
};

/**
 * writeAnsweredSettings puts the settings a settings write answered with into the organization
 * read they ride, the rest of the organization as it was read.
 */
const writeAnsweredSettings = (
  queryClient: QueryClient,
  organizationId: string,
  answer: unknown,
): void => {
  const answered = organizationSettingsSchema.safeParse(answer);
  if (!answered.success) {
    return;
  }

  queryClient.setQueryData<ConsoleOrganization>(organizationQueryKey(organizationId), (held) =>
    held === undefined ? held : { ...held, settings: answered.data },
  );
};

/**
 * writeAnsweredPublishableKey puts the key an edit answered with in place of the one the
 * organization's key list holds under its id, the other keys as they were read.
 */
const writeAnsweredPublishableKey = (
  queryClient: QueryClient,
  organizationId: string,
  answer: unknown,
): void => {
  const answered = publishableKeySchema.safeParse(answer);
  if (!answered.success) {
    return;
  }

  queryClient.setQueryData<ConsolePublishableKey[]>(
    publishableKeysQueryKey(organizationId),
    (held) => held?.map((key) => (key.id === answered.data.id ? answered.data : key)),
  );
};

/**
 * writeAnsweredSecretKey puts the secret key a revoke answered with in place of the one the
 * organization's key list holds under its id, the other keys as they were read.
 */
const writeAnsweredSecretKey = (
  queryClient: QueryClient,
  organizationId: string,
  answer: unknown,
): void => {
  const answered = secretKeySchema.safeParse(answer);
  if (!answered.success) {
    return;
  }

  queryClient.setQueryData<SecretKey[]>(secretKeysQueryKey(organizationId), (held) =>
    held?.map((key) => (key.id === answered.data.id ? answered.data : key)),
  );
};

/**
 * useUpdateOrganization renames an organization. On success the organization the rename answers
 * goes into the organization read at once, its new `etag` with it, so the next rename is made on
 * the version this one wrote even when the refetch that follows fails; the name is denormalized
 * onto the user's organization list, so both the organization read and the org list are then
 * invalidated. A rename refused with a 412 (somebody changed the organization since the page
 * read it) reloads the organization read, so the page shows what is there now; the caller says
 * so. A rename whose answer was lost after it was made, or that is still being made
 * ({@link isWriteMadeOrRunning}), reloads both, as a landed rename does.
 */
export const useUpdateOrganization = (config: RestClientConfig, organizationId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ ifMatch, params }: ConditionalWrite<Parameters<typeof updateOrganization>[2]>) =>
      updateOrganization(config, organizationId, params, ifMatch),
    onError: (error) => {
      if (isWriteMadeOrRunning(error)) {
        return Promise.all([
          queryClient.invalidateQueries({
            exact: true,
            queryKey: organizationQueryKey(organizationId),
          }),
          queryClient.invalidateQueries({ queryKey: userOrganizationsQueryKey }),
        ]);
      }

      return isPreconditionFailedError(error)
        ? queryClient.invalidateQueries({
            exact: true,
            queryKey: organizationQueryKey(organizationId),
          })
        : undefined;
    },
    onSuccess: (organization) => {
      writeAnsweredOrganization(queryClient, organizationId, organization);

      return Promise.all([
        queryClient.invalidateQueries({
          exact: true,
          queryKey: organizationQueryKey(organizationId),
        }),
        queryClient.invalidateQueries({ queryKey: userOrganizationsQueryKey }),
      ]);
    },
  });
};

/**
 * SettingsWrite is what a settings save hands {@link useUpdateOrganizationSettings}: the settings
 * its page read, their `etag` with them, and the change, built on the settings the write is made
 * on.
 *
 * The change is built when the write runs rather than when it is asked for, because the groups a
 * settings write sends are replaced whole: a card that spread its own fields over the default
 * props its page read, while another card's write was still out, would put back every default
 * prop that write changed. Built on the settings it is made on, it carries them through. A change
 * that reads none of them (the theme) ignores its argument.
 */
export interface SettingsWrite {
  readonly change: (settings: ConsoleOrganizationSettings) => OrganizationSettingsPatchParams;
  readonly read: ConsoleOrganizationSettings;
}

/**
 * settingsMutationKey keys an organization's settings writes by the organization, for the reason
 * {@link billingMutationKey} gives: a write still queued behind another runs with the options it
 * was sent with, never with those of a page that rendered since for another organization.
 */
const settingsMutationKey = (organizationId: string) =>
  [...organizationQueryKey(organizationId), "settings"] as const;

/**
 * SETTINGS_WRITES_KEPT is how many of an organization's own settings writes this console
 * remembers ({@link ownSettingsWrites}). A write only ever chains through the few made while it
 * was waiting its turn, so a handful is plenty, and the bound keeps a long session's record of
 * safety rules, boundaries included, from growing for as long as the page stays open.
 */
const SETTINGS_WRITES_KEPT = 16;

/**
 * ownSettingsWrites remembers, per query client and organization, what each settings write this
 * console made answered, under the `etag` it was made on: the tags this console's own writes
 * produced, and the settings each one carries.
 *
 * It is what tells a version this console wrote from one somebody else did. A write waiting its
 * turn behind another was asked for on the version its page read, which the write before it has
 * since replaced: it is made on the version that write produced, and on nothing else. A version
 * somebody else produced is never followed: the write names the last version this console's own
 * writes produced, which is no longer the one held, and is refused with the 412 that says so.
 * The record is kept per query client so two clients (two test cases, for one) never share it,
 * and taken from what each write answered rather than from the organization read, which a refetch
 * served a moment behind may hold a version back.
 */
const ownSettingsWrites = new WeakMap<
  QueryClient,
  Map<string, Map<string, ConsoleOrganizationSettings>>
>();

/** settingsWritesOf returns the organization's record of its own settings writes. */
const settingsWritesOf = (
  queryClient: QueryClient,
  organizationId: string,
): Map<string, ConsoleOrganizationSettings> => {
  const byOrganization =
    ownSettingsWrites.get(queryClient) ??
    new Map<string, Map<string, ConsoleOrganizationSettings>>();
  ownSettingsWrites.set(queryClient, byOrganization);
  const writes =
    byOrganization.get(organizationId) ?? new Map<string, ConsoleOrganizationSettings>();
  byOrganization.set(organizationId, writes);

  return writes;
};

/**
 * recordSettingsWrite remembers what a settings write made on the version `madeOn` answered. An
 * answer that does not parse, or that names the version it was made on, records nothing: there is
 * no new version of this console's to follow.
 */
const recordSettingsWrite = (
  writes: Map<string, ConsoleOrganizationSettings>,
  madeOn: string,
  answer: unknown,
): void => {
  const answered = organizationSettingsSchema.safeParse(answer);
  if (!answered.success || answered.data.etag === madeOn) {
    return;
  }

  writes.delete(madeOn);
  writes.set(madeOn, answered.data);
  for (const oldest of writes.keys()) {
    if (writes.size <= SETTINGS_WRITES_KEPT) {
      break;
    }

    writes.delete(oldest);
  }
};

/**
 * settingsWriteBase is the version a settings write is made on: the settings its page read, or,
 * where this console's own writes have replaced that version since, the version the last of them
 * produced, followed write by write. Each step is bounded by the record's size, so no answer can
 * hold the walk in a loop.
 */
const settingsWriteBase = (
  writes: ReadonlyMap<string, ConsoleOrganizationSettings>,
  read: ConsoleOrganizationSettings,
): ConsoleOrganizationSettings => {
  let base = read;
  for (let step = 0; step < writes.size; step += 1) {
    const next = writes.get(base.etag);
    if (next === undefined) {
      break;
    }

    base = next;
  }

  return base;
};

/**
 * useUpdateOrganizationSettings applies an owner/admin partial update of the organization's
 * self-serve settings (any subset of `default_props`, `theme`, `safety_rules`), conditional on
 * the settings' own `etag`.
 *
 * An organization's settings writes run one after another, whichever card or page sent them (one
 * mutation `scope` per organization), and each is built when its turn comes ({@link SettingsWrite}).
 * It is made on the settings its page read, unless this console's own writes have replaced that
 * version meanwhile: it is then made on the version they produced, their changes carried through,
 * so two cards saved together both land. A version somebody else produced is never followed, so a
 * write over one is refused with a 412 ({@link ownSettingsWrites}).
 *
 * On success the settings the write answers go into the organization read at once, their new
 * `etag` with them, so a card saved next, the refetch of this write still out or failed, is
 * conditional on the version this one wrote. It then invalidates the organization read (so the
 * Settings/Security pages and the embedded-studio defaults pick up the new values) and the user's
 * organization list (the settings ride the org read the shell shares), and the next write waits
 * for both. A write refused for changing a staff-controlled safety rule, or refused with a 412
 * because somebody changed the settings since the page read them, invalidates the organization
 * read too, since what it re-sent was stale. So does a write whose answer was lost after it was
 * made, or that is still being made ({@link isWriteMadeOrRunning}): the settings it wrote are
 * not in the read, and only a read of them carries their new `etag`. UX side effects (a success
 * notification) are attached by the caller via mutate(vars, { onSuccess }).
 */
export const useUpdateOrganizationSettings = (config: RestClientConfig, organizationId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ change, read }: SettingsWrite) => {
      const writes = settingsWritesOf(queryClient, organizationId);
      const base = settingsWriteBase(writes, read);
      const answer = await updateOrganizationSettings(
        config,
        organizationId,
        change(base),
        base.etag,
      );
      recordSettingsWrite(writes, base.etag, answer);

      return answer;
    },
    mutationKey: settingsMutationKey(organizationId),
    // A write refused for changing a rule the agreement fixes means the page read the rules
    // before staff last set them, a 412 that somebody else wrote since, and an idempotency 409
    // that this write landed or is landing unseen: either way the read is replaced, so the page
    // shows what is in force.
    onError: (error) =>
      isStaffControlledError(error) ||
      isPreconditionFailedError(error) ||
      isWriteMadeOrRunning(error)
        ? queryClient.invalidateQueries({
            exact: true,
            queryKey: organizationQueryKey(organizationId),
          })
        : undefined,
    onSuccess: (settings) => {
      writeAnsweredSettings(queryClient, organizationId, settings);

      return Promise.all([
        queryClient.invalidateQueries({
          exact: true,
          queryKey: organizationQueryKey(organizationId),
        }),
        queryClient.invalidateQueries({ queryKey: userOrganizationsQueryKey }),
      ]);
    },
    scope: { id: `organization-settings:${organizationId}` },
  });
};

/**
 * useInviteOrganizationMembers invites every address asked for, one request each
 * ({@link inviteOrganizationMembers}), and resolves to what became of each: it never rejects for a
 * refusal, which is an outcome of its address. Once the walk ends it refreshes the pending
 * invitations, and the account exactly, because an invitation holds its seat from the moment it
 * is made and the seats in use ride the account's `limit_usage`: a seat refusal says the page
 * counted fewer seats in use than the server does, and the form gates the next invitation on
 * that count.
 */
export const useInviteOrganizationMembers = (config: RestClientConfig, organizationId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (invitations: Parameters<typeof inviteOrganizationMembers>[2]) =>
      inviteOrganizationMembers(config, organizationId, invitations),
    onSettled: () =>
      Promise.all([
        queryClient.invalidateQueries({
          queryKey: organizationInvitationsQueryKey(organizationId),
        }),
        queryClient.invalidateQueries({
          exact: true,
          queryKey: organizationAccountQueryKey(organizationId),
        }),
      ]),
  });
};

/**
 * useRevokeOrganizationInvitation withdraws an invitation still waiting for an answer; the
 * mutation variable is the invitation id. On success it refreshes the pending invitations and the
 * account exactly, since the withdrawn invitation frees the seat its `limit_usage` counted.
 */
export const useRevokeOrganizationInvitation = (
  config: RestClientConfig,
  organizationId: string,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (invitationId: string) =>
      revokeOrganizationInvitation(config, organizationId, invitationId),
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({
          queryKey: organizationInvitationsQueryKey(organizationId),
        }),
        queryClient.invalidateQueries({
          exact: true,
          queryKey: organizationAccountQueryKey(organizationId),
        }),
      ]),
  });
};

/**
 * useUpdateOrganizationMemberRole changes a member's role. Once it settles it invalidates the
 * member list and the user's org list (the acting user's own role could change), on a refusal
 * too: a member who left meanwhile (404) or was made an admin by the owner (403) is shown as they
 * now stand rather than as the page listed them.
 */
export const useUpdateOrganizationMemberRole = (
  config: RestClientConfig,
  organizationId: string,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (vars: { role: AssignableOrganizationRole; userId: string }) =>
      updateOrganizationMemberRole(config, organizationId, vars.userId, vars.role),
    onSettled: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: organizationMembersQueryKey(organizationId) }),
        queryClient.invalidateQueries({ queryKey: userOrganizationsQueryKey }),
      ]),
  });
};

/**
 * useRemoveOrganizationMember removes another member of the organization; the mutation variable
 * is the member's user id. Once it settles it invalidates the member list, the user's org list and
 * the account exactly, since a removed member frees the seat its `limit_usage` counted, on a
 * refusal too, so a member whose role changed meanwhile reads as it now stands. Leaving, the
 * signed-in user removing themself, is {@link useLeaveOrganization}.
 */
export const useRemoveOrganizationMember = (config: RestClientConfig, organizationId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (userId: string) => removeOrganizationMember(config, organizationId, userId),
    onSettled: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: organizationMembersQueryKey(organizationId) }),
        queryClient.invalidateQueries({ queryKey: userOrganizationsQueryKey }),
        queryClient.invalidateQueries({
          exact: true,
          queryKey: organizationAccountQueryKey(organizationId),
        }),
      ]),
  });
};

/**
 * useLeaveOrganization takes the signed-in user out of the organization: it deletes their own
 * membership, the mutation variable being their user id.
 *
 * Once it lands, nothing under the organization is the user's to read any more, so every read
 * under it is removed from the cache rather than asked again: a refresh of the member list or
 * the account would only be answered 404. Any of them still out is cancelled first, so none
 * lands in the cache after the removal. The memberships are then read again, and the mutation
 * stays pending until they answer, so the switcher and the navigation have dropped the
 * organization by the time the caller takes the reader elsewhere.
 */
export const useLeaveOrganization = (config: RestClientConfig, organizationId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (userId: string) => removeOrganizationMember(config, organizationId, userId),
    onSuccess: async () => {
      const left = organizationQueryKey(organizationId);
      await queryClient.cancelQueries({ queryKey: left });
      queryClient.removeQueries({ queryKey: left });
      await queryClient.invalidateQueries({ queryKey: userOrganizationsQueryKey });
    },
  });
};

/**
 * useTransferOrganizationOwnership makes another member the owner and the signed-in owner an
 * admin; the mutation variable is the new owner's user id. Once it settles it invalidates the
 * member list and the user's org list, where the signed-in user's own role changed, on a failure
 * too: a target who left meanwhile (404) drops out of the list, and a transfer whose answer was
 * lost after it landed (409 `IDEMPOTENCY_ANSWER_UNAVAILABLE`) shows its new owner. What the route
 * settled from the old role (the access every organization page reads) is the caller's to
 * refresh, by invalidating the router.
 */
export const useTransferOrganizationOwnership = (
  config: RestClientConfig,
  organizationId: string,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (userId: string) => transferOrganizationOwnership(config, organizationId, userId),
    onSettled: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: organizationMembersQueryKey(organizationId) }),
        queryClient.invalidateQueries({ queryKey: userOrganizationsQueryKey }),
      ]),
  });
};

/**
 * refreshScopeGates asks the organization's account read again when a key write was refused
 * with a 402: a scope whose product the organization no longer holds, which the account the
 * form gated its scopes on still said it did. Read again, the form holds that scope shut. Any
 * other refusal says nothing about the products, and is left alone.
 */
const refreshScopeGates = (
  queryClient: QueryClient,
  organizationId: string,
  error: Error,
): Promise<void> | undefined =>
  isPaymentRequiredError(error)
    ? queryClient.invalidateQueries({
        exact: true,
        queryKey: organizationAccountQueryKey(organizationId),
      })
    : undefined;

/**
 * useCreatePublishableKey creates a publishable key, under the `idempotencyKey` the caller holds
 * across its repeats when it passes one. On success it invalidates
 * the publishable-key list; refused with a 402, the account read the scope gates are read
 * from ({@link refreshScopeGates}). A create whose answer was lost after it was made, or that is
 * still being made ({@link isWriteMadeOrRunning}), made a key the list does not show yet, so the
 * list is read again as after a success.
 */
export const useCreatePublishableKey = (config: RestClientConfig, organizationId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      idempotencyKey,
      ...params
    }: Parameters<typeof createPublishableKey>[2] & { readonly idempotencyKey?: string }) =>
      createPublishableKey(config, organizationId, params, idempotencyKey),
    onError: (error) =>
      isWriteMadeOrRunning(error)
        ? queryClient.invalidateQueries({ queryKey: publishableKeysQueryKey(organizationId) })
        : refreshScopeGates(queryClient, organizationId, error),
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: publishableKeysQueryKey(organizationId) }),
      ]),
  });
};

/**
 * useUpdatePublishableKey updates a publishable key's name/scopes, conditional on the `etag` the
 * key was read under. On success the key the edit answers goes into the publishable-key list at
 * once, its new `etag` with it, so the next edit or revoke of that key is made on the version this
 * one wrote even when the refetch that follows fails. Once it settles it invalidates the
 * publishable-key list, on a 412 too, so the page shows the key as it now stands; refused with
 * a 402, the account read the scope gates are read from too ({@link refreshScopeGates}).
 */
export const useUpdatePublishableKey = (config: RestClientConfig, organizationId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (
      vars: ConditionalWrite<Parameters<typeof updatePublishableKey>[3]> & { keyId: string },
    ) => updatePublishableKey(config, organizationId, vars.keyId, vars.params, vars.ifMatch),
    onError: (error) => refreshScopeGates(queryClient, organizationId, error),
    // A 412 says the list the form was seeded from is stale: it is read again either way.
    onSettled: () =>
      queryClient.invalidateQueries({ queryKey: publishableKeysQueryKey(organizationId) }),
    onSuccess: (key) => writeAnsweredPublishableKey(queryClient, organizationId, key),
  });
};

/**
 * useDeletePublishableKey revokes a publishable key, conditional on the `etag` the key was read
 * under. On success the revoked key the API answers with takes the key's place in the
 * publishable-key list at once, its `revoked_at` with it, so the key leaves the live keys the
 * page lists even when the refetch that follows fails. Once it settles it invalidates the
 * publishable-key list, on a 412 too.
 */
export const useDeletePublishableKey = (config: RestClientConfig, organizationId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (vars: { ifMatch: string | undefined; keyId: string }) =>
      deletePublishableKey(config, organizationId, vars.keyId, vars.ifMatch),
    // A 412 says the key changed since the confirm showed it: the list is read again either way.
    onSettled: () =>
      queryClient.invalidateQueries({ queryKey: publishableKeysQueryKey(organizationId) }),
    onSuccess: (key) => writeAnsweredPublishableKey(queryClient, organizationId, key),
  });
};

/**
 * useCreateSecretKey creates a secret key. The mutation result carries the
 * one-time secret `key` the reveal-once modal shows. Once it settles it invalidates
 * the secret-key list, on a failure too: a create whose answer was lost after it committed
 * (409 `IDEMPOTENCY_ANSWER_UNAVAILABLE`) made a key whose value is never answered again, and the
 * list is where the person finds it to revoke.
 */
export const useCreateSecretKey = (config: RestClientConfig, organizationId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: Parameters<typeof createSecretKey>[2]) =>
      createSecretKey(config, organizationId, params),
    onSettled: () =>
      queryClient.invalidateQueries({ queryKey: secretKeysQueryKey(organizationId) }),
  });
};

/**
 * useUpdateSecretKey renames a secret key. On success it invalidates the
 * secret-key list.
 */
export const useUpdateSecretKey = (config: RestClientConfig, organizationId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (vars: { keyId: string; params: Parameters<typeof updateSecretKey>[3] }) =>
      updateSecretKey(config, organizationId, vars.keyId, vars.params),
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: secretKeysQueryKey(organizationId) }),
      ]),
  });
};

/**
 * useDeleteSecretKey revokes a secret key. On success the revoked key the API answers with takes
 * the key's place in the secret-key list at once, its `revoked_at` with it, so the key leaves the
 * live keys the page lists even when the refetch that follows fails; the list is then
 * invalidated.
 */
export const useDeleteSecretKey = (config: RestClientConfig, organizationId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (keyId: string) => deleteSecretKey(config, organizationId, keyId),
    onSuccess: (key) => {
      writeAnsweredSecretKey(queryClient, organizationId, key);

      return queryClient.invalidateQueries({ queryKey: secretKeysQueryKey(organizationId) });
    },
  });
};

/**
 * ConfirmPaymentMethodCallbacks are what a caller does once an attach settles. They are the
 * mutation's own rather than one call's: a callback handed to `mutate` is dropped when the
 * component that called it unmounts first, and an attach a redirect brought back must still
 * hold or release its card, and say so, wherever the reader went meanwhile.
 */
export interface ConfirmPaymentMethodCallbacks {
  readonly onError?:
    | ((error: Error, params: OrganizationBillingPaymentMethodParams) => void)
    | undefined;
  readonly onSuccess?:
    | ((paymentMethod: PaymentMethod, params: OrganizationBillingPaymentMethodParams) => void)
    | undefined;
}

/**
 * billingMutationKey keys a billing write by the organization it is for, and by what it writes.
 *
 * The organization is in the key for a reason beyond telling writes apart. A mutation observer
 * hands a call still in flight the options of every render that follows, unless the key moved:
 * a page that renders for another organization while an attach or a run is out would otherwise
 * finish it with the other organization's callbacks, holding a card on the wrong organization
 * or refreshing a read that never changed. A key that names the organization detaches the call
 * instead, and it settles with the options it was sent with.
 */
export const billingMutationKey = (
  organizationId: string,
  write: "paymentMethod" | "subscription.end" | "subscription.run",
) => [...organizationQueryKey(organizationId), "billing", write] as const;

/**
 * useConfirmOrganizationBillingPaymentMethod attaches the card a succeeded SetupIntent
 * collected to the organization's Stripe customer; the mutation variable is the succeeded
 * SetupIntent id. The billing page runs it for a card a redirect method confirmed away from
 * the page. On success the backend has pinned the card and, for the first one, opened the
 * Data API subscription, so it invalidates every read under the organization, the account
 * (which carries the entitlements, the subscription summary and the can_manage_billing flag the
 * gates branch on) included, so the billing surface pulls the re-materialized state. The attach
 * also pays the invoices the organization still has open on the card, so the invoice history is
 * read again too, and both are read again for an attach whose answer was lost after it was made
 * ({@link isWriteMadeOrRunning}), which the caller reads as attached. The caller's own side
 * effects ride `callbacks`, run whether or not it is still mounted, and always for the
 * organization the call was sent for ({@link billingMutationKey}).
 */
export const useConfirmOrganizationBillingPaymentMethod = (
  config: RestClientConfig,
  organizationId: string,
  callbacks: ConfirmPaymentMethodCallbacks = {},
) => {
  const queryClient = useQueryClient();
  const refreshAttached = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: organizationQueryKey(organizationId) }),
      queryClient.invalidateQueries({ queryKey: invoicesQueryKey(organizationId) }),
    ]);

  return useMutation({
    mutationFn: (params: OrganizationBillingPaymentMethodParams) =>
      confirmOrganizationBillingPaymentMethod(config, organizationId, params),
    mutationKey: billingMutationKey(organizationId, "paymentMethod"),
    onError: async (error, params) => {
      callbacks.onError?.(error, params);

      // An attach whose answer was lost after it was made, or that is still being made, changed
      // what a landed attach changes.
      if (isWriteMadeOrRunning(error)) {
        await refreshAttached();
      }
    },
    onSuccess: async (paymentMethod, params) => {
      callbacks.onSuccess?.(paymentMethod, params);

      await refreshAttached();
    },
  });
};

/**
 * writeSubscriptionSummary seeds the account read with the summary an end or a run answered
 * with, so a page shows the subscription as the write left it at once, while the read that
 * follows ({@link settleSubscriptionWrite}) is still out.
 */
const writeSubscriptionSummary = (
  queryClient: QueryClient,
  organizationId: string,
  summary: OrganizationSubscriptionSummary | null,
): void => {
  queryClient.setQueryData<OrganizationAccount>(
    organizationAccountQueryKey(organizationId),
    (held) => {
      if (held === undefined) {
        return held;
      }

      // A null answer is an end that found nothing left to report: the account then holds no
      // subscription, which it says by leaving the member out rather than nulling it.
      const { subscription: _replaced, ...rest } = held;

      return summary === null ? rest : { ...rest, subscription: summary };
    },
  );
};

/**
 * settleSubscriptionWrite asks the account read again once an end or a run settles, landed or
 * refused, and only that entry of the organization's reads (`exact`): the account carries what
 * the organization holds and its subscription summary, which is everything a run or an end
 * changes there. The mutation stays pending until the read has answered, so a control reading
 * `isPending`, and a notification the caller attaches to the call, wait for the page to show
 * the new state rather than announcing it beside the old one.
 *
 * A landed write has already written its summary into the read ({@link writeSubscriptionSummary})
 * and the read is asked again at once all the same: a run that opened a subscription also changed
 * what the organization holds (`data` among its capabilities), which the summary does not carry.
 * The account read cannot put the subscription as it stood before the write back over the
 * written summary, because a billing write fences it: for 30 seconds after one, the organization's
 * entitlements and its subscription summary are read through the writer (ADR 0134), so the
 * refetch answers what the write stored.
 *
 * A refused write wrote nothing, and the refusal (a 402 with no card to open a subscription on)
 * says the read is stale, so it is asked again the same way.
 *
 * A run also pays the invoices the organization still has open on its default card, so `paid`
 * re-reads the invoice history alongside.
 */
const settleSubscriptionWrite = async (
  queryClient: QueryClient,
  organizationId: string,
  paid: boolean,
): Promise<void> => {
  await Promise.all([
    queryClient.invalidateQueries({
      exact: true,
      queryKey: organizationAccountQueryKey(organizationId),
    }),
    paid
      ? queryClient.invalidateQueries({ queryKey: invoicesQueryKey(organizationId) })
      : undefined,
  ]);
};

/**
 * useEndOrganizationBillingSubscription ends the organization's Data API subscription at the end
 * of its period. The account read carries the subscription summary every billing surface and
 * banner reads: the summary the end answers with is written into it at once, so a page never
 * shows the subscription it just ended as running, and the read is then asked again
 * ({@link settleSubscriptionWrite}). UX side effects are attached by the caller via
 * mutate(undefined, { onSuccess }).
 */
export const useEndOrganizationBillingSubscription = (
  config: RestClientConfig,
  organizationId: string,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => endOrganizationBillingSubscription(config, organizationId),
    mutationKey: billingMutationKey(organizationId, "subscription.end"),
    onSettled: () => settleSubscriptionWrite(queryClient, organizationId, false),
    onSuccess: (summary) => writeSubscriptionSummary(queryClient, organizationId, summary),
  });
};

/**
 * useRunOrganizationBillingSubscription runs the organization's Data API: it takes back a
 * scheduled end, or opens a subscription on the default payment method where none is held. The
 * summary it answers with is written into the account read at once, and the read is then asked
 * again, which brings what a run that opened a subscription changed of what the organization
 * holds, with the invoice history the run may have paid ({@link settleSubscriptionWrite}). UX
 * side effects (a 402 opening the
 * card dialog, a notification) are attached by the caller via
 * mutate(undefined, { onError, onSuccess }).
 *
 * Every surface that runs the Data API for an organization shares one key, so each can tell a
 * run already out ({@link useIsRunningOrganizationBillingSubscription}) and none sends a second.
 */
export const useRunOrganizationBillingSubscription = (
  config: RestClientConfig,
  organizationId: string,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => runOrganizationBillingSubscription(config, organizationId),
    mutationKey: billingMutationKey(organizationId, "subscription.run"),
    // A run whose answer was lost after it was made, or that is still being made, may have paid
    // what a landed run pays.
    onSettled: (_summary, error) =>
      settleSubscriptionWrite(
        queryClient,
        organizationId,
        error === null || isWriteMadeOrRunning(error),
      ),
    onSuccess: (summary) => writeSubscriptionSummary(queryClient, organizationId, summary),
  });
};

/**
 * useIsRunningOrganizationBillingSubscription reports whether a run of the organization's Data
 * API is out, from whichever surface sent it: the plan card and the banner each hold their own
 * mutation, and a second click on either while the first run is out must send nothing.
 */
export const useIsRunningOrganizationBillingSubscription = (organizationId: string): boolean =>
  useIsMutating({ mutationKey: billingMutationKey(organizationId, "subscription.run") }) > 0;
